// Admin SessionsPage — every session across every course (the existing
// GET /api/sessions with no courseId already returns this for an admin
// caller), with a course filter, most-recent-first sort, and client-side
// pagination. "Students checked in / Total enrolled" per row is fetched
// only for the current page's sessions (bounded by PAGE_SIZE), not the
// whole dataset.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getJson } from "../../hooks/useApi.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import Badge from "../../components/shared/Badge.js";
import { formatDateTime } from "../../lib/formatDateTime.js";
import type { AttendanceRecord, PaginatedResult } from "@dm-lms/shared/types";

const PAGE_SIZE = 20;
const selectClass = "rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy";

export default function SessionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const courseIdFilter = searchParams.get("courseId") ?? "";
  const [page, setPage] = useState(1);

  const { sessions: sessionsData, isLoading, error } = useSessions();

  const courseOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const session of sessionsData) map.set(session.courseId, session.courseTitle);
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [sessionsData]);

  const filteredSorted = useMemo(() => {
    const filtered = courseIdFilter ? sessionsData.filter((session) => session.courseId === courseIdFilter) : sessionsData;
    return [...filtered].sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());
  }, [sessionsData, courseIdFilter]);

  const total = filteredSorted.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = useMemo(
    () => filteredSorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredSorted, page]
  );

  const [enrolledByCourse, setEnrolledByCourse] = useState<Map<string, number>>(new Map());
  const [presentBySession, setPresentBySession] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (pageItems.length === 0) return;
    let cancelled = false;

    const uniqueCourseIds = Array.from(new Set(pageItems.map((session) => session.courseId)));
    void Promise.all(
      uniqueCourseIds.map((courseId) =>
        getJson<PaginatedResult<unknown>>(`/api/enrollments/course/${courseId}?page=1&pageSize=1`).then(
          (res) => [courseId, res.ok ? res.data.total : 0] as const
        )
      )
    ).then((entries) => {
      if (!cancelled) setEnrolledByCourse(new Map(entries));
    });

    void Promise.all(
      pageItems.map((session) =>
        getJson<AttendanceRecord[]>(`/api/attendance/session/${session.id}`).then(
          (res) => [session.id, res.ok ? res.data.filter((a) => a.status === "present").length : 0] as const
        )
      )
    ).then((entries) => {
      if (!cancelled) setPresentBySession(new Map(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [pageItems]);

  function changeCourseFilter(courseId: string) {
    setSearchParams(courseId ? { courseId } : {});
    setPage(1);
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Sessions</h1>

      <div className="mb-4">
        <select
          value={courseIdFilter}
          onChange={(event) => changeCourseFilter(event.target.value)}
          className={selectClass}
        >
          <option value="">All courses</option>
          {courseOptions.map((course) => (
            <option key={course.id} value={course.id}>
              {course.title}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-dm-slate text-sm">Loading sessions…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && (
        <>
          {total === 0 ? (
            <p className="text-dm-slate text-sm">No sessions yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Course</th>
                    <th className="px-4 py-3 font-medium">Mode</th>
                    <th className="px-4 py-3 font-medium">Scheduled</th>
                    <th className="px-4 py-3 font-medium">Duration</th>
                    <th className="px-4 py-3 font-medium">Checked in / Enrolled</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageItems.map((session) => {
                    const isUpcoming = new Date(session.scheduledAt).getTime() > Date.now();
                    const present = presentBySession.get(session.id) ?? 0;
                    const enrolled = enrolledByCourse.get(session.courseId) ?? 0;
                    return (
                      <tr
                        key={session.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/app/admin/sessions/${session.id}`)}
                      >
                        <td className="px-4 py-3 font-medium text-dm-navy">{session.title}</td>
                        <td className="px-4 py-3">{session.courseTitle}</td>
                        <td className="px-4 py-3">
                          <Badge tone="gray">{session.mode}</Badge>
                        </td>
                        <td className="px-4 py-3">{formatDateTime(session.scheduledAt)}</td>
                        <td className="px-4 py-3">{session.durationMinutes} min</td>
                        <td className="px-4 py-3">
                          {present} / {enrolled}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={isUpcoming ? "blue" : "gray"}>{isUpcoming ? "Upcoming" : "Past"}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/app/admin/sessions/${session.id}`}
                            onClick={(event) => event.stopPropagation()}
                            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-dm-navy hover:bg-gray-50"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="text-xs text-dm-slate">
                Page {page} of {totalPages} · {total} total
              </span>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
